import { useFragment } from 'react-relay';
import { DropzoneProps, useDropzone } from 'react-dropzone';
import graphql from 'babel-plugin-relay/macro';

import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import ConfirmableIconButton from 'components/ConfirmableIconButton';

import { green, red } from '@mui/material/colors';
import { humanFileSize } from 'lib';
import { StoredFile_file$key } from '__generated__/StoredFile_file.graphql';

type Props = DropzoneProps & {
  file: StoredFile_file$key | null;
  loading: boolean;
  rejectMessage?: string;
  acceptMessage?: string;
  defaultMessage?: string;
  onDelete?: () => void;
};

const StoredFile = (props: Props) => {
  const {
    file,
    loading,
    rejectMessage = 'Netinkamas failo formatas?',
    acceptMessage = 'Pradėti įkėlimą?',
    defaultMessage = 'Tempkite failą arba spauskite čia',
    onDelete,
    ...rest
  } = props;

  const storedFile = useFragment(
    graphql`
      fragment StoredFile_file on StoredFile {
        fileName
        size
      }
    `,
    file
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone(rest);

  const text = isDragActive
    ? isDragReject
      ? rejectMessage
      : acceptMessage
    : defaultMessage;

  const borderColor = isDragActive
    ? isDragReject
      ? red[500]
      : green[500]
    : null;

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderColor,
      }}
      {...getRootProps()}>
      <CardHeader
        title={storedFile?.fileName ?? text}
        subheader={storedFile?.size && humanFileSize(storedFile.size)}
        avatar={<FileUploadIcon />}
        sx={{ flexGrow: 1 }}
      />

      <CardContent>
        {loading ? (
          <CircularProgress size="1.4rem" />
        ) : (
          storedFile && (
            <ConfirmableIconButton size="small" onConfirm={onDelete}>
              <DeleteIcon fontSize="small" />
            </ConfirmableIconButton>
          )
        )}
      </CardContent>

      <input {...getInputProps()} />
    </Card>
  );
};

export default StoredFile;
